import { Box, Icon, Skeleton, Text } from '@factorialco/deprecated-design-system'
import DOMPurify from 'dompurify'
import words from 'lodash/words'
import * as math from 'mathjs'

import i18n from 'modules/core/lib/i18n'

import { ResultValidationFormula } from 'modules/payroll_bundle/hooks/useResultValidations'
import {
  ResultValidationsItem,
  useResultValidationsItems,
} from 'modules/payroll_bundle/hooks/useResultValidationsItems'
import { formatAmount } from 'modules/payroll_bundle/lib/formatNumber'

import * as styles from './index.vanilla.css'

interface FormulaProps {
  policyPeriodId: number
  employeeIds?: number[]
  validationFormula: ResultValidationFormula
  currency: string
}

const Formula = ({ policyPeriodId, employeeIds, validationFormula, currency }: FormulaProps) => {
  const { data, fetching } = useResultValidationsItems(policyPeriodId, employeeIds)

  const buildExpressionUi = (expression: string, data: ResultValidationsItem[]) => {
    const snakeCaseWordRegex = /\b[a-z]+(?:_[a-z]+)*\b/g

    // "(contract_gross_salary / 12) - (supplements_bonus + supplements_overtime)"
    // => ['contract_gross_salary', 'supplements_bonus', 'supplements_overtime']
    const selectors = words(expression, snakeCaseWordRegex)

    const selectorToAmount: Record<string, number> = selectors.reduce((obj, currentSelector) => {
      const totalForSelector: number = data
        .filter((value) => value.selector == currentSelector)
        .reduce((sum, value) => sum + value.amount, 0)
      return { ...obj, [currentSelector]: totalForSelector }
    }, {})

    // Business rule: if a value was not given for a selector, consider it zero
    const safeSelectorToAmount: Record<string, number> = selectors.reduce(
      (acc, element) => ({ ...acc, [element]: selectorToAmount[element] || 0 }),
      selectorToAmount
    )

    // Remove unnecessary parens; hide implicit multiplication
    const htmlOptions = { parenthesis: 'auto', implicit: 'hide' }
    const expressionHTML = math.parse(expression).toHTML(htmlOptions)

    const html = selectors.reduce((accumulator, selector) => {
      const name = data.find((element) => element.selector === selector)?.name || selector
      return accumulator.replace(
        selector,
        `${name} <span class="math-symbol-value">(${formatAmount(
          safeSelectorToAmount[selector] || 0,
          currency
        )})</span>`
      )
    }, expressionHTML)

    return html
  }

  return (
    <>
      {fetching && (
        <Box flexDirection="row" flexWrap="wrap" width="s400" gap="s16" padding="s12">
          <Skeleton width="s48" height="s16" isLoading />
          <Skeleton width="s64" height="s16" isLoading />
          <Skeleton width="s48" height="s16" isLoading />
          <Skeleton width="s96" height="s16" isLoading />
          <Skeleton width="s48" height="s16" isLoading />
          <Skeleton width="s64" height="s16" isLoading />
          <Skeleton width="s64" height="s16" isLoading />
          <Skeleton width="s96" height="s16" isLoading />
          <Skeleton width="s48" height="s16" isLoading />
          <Skeleton width="s96" height="s16" isLoading />
        </Box>
      )}
      {!fetching && (
        <Box flexDirection="column" gap="s12" padding="s12" background="grey100">
          <Box flexDirection="row">
            {/* Input is only provided by CLI, not by users */}
            <div
              className={styles.mathElement}
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(
                  buildExpressionUi(validationFormula.inputsExpression, data)
                ),
              }}
            />
          </Box>

          <Box flexDirection="column">
            <Box
              style={{ top: '0px', left: '16px', position: 'relative' }}
              height="s16"
              border={{
                left: { width: 's1', style: 'solid', color: 'grey300' },
              }}
            ></Box>

            <Box
              flexDirection="row"
              alignItems="center"
              justifyContent="flexStart"
              style={{ width: '150px' }}
              background="grey300"
              gap="s4"
              borderRadius={{ all: 'abs016' }}
              paddingTop="s4"
              paddingRight="s8"
              paddingBottom="s4"
              paddingLeft="s8"
            >
              <Icon.Small icon="equals" color="grey800" />
              <Text.Normal size="200" color="grey800">
                {i18n.t('payroll_bundle.results_payments.results.validations.must_be_equal')}
              </Text.Normal>
            </Box>
            <Box
              style={{ top: '0px', left: '16px', position: 'relative', overflow: 'auto' }}
              height="s16"
              border={{
                left: { width: 's1', style: 'solid', color: 'grey300' },
              }}
            ></Box>
          </Box>

          <Box flexDirection="row">
            {/* Input is sanitized in backend */}
            {/* nosemgrep: react-dangerouslysetinnerhtml */}
            <div
              className={styles.mathElement}
              dangerouslySetInnerHTML={{
                __html: buildExpressionUi(validationFormula.resultsExpression, data),
              }}
            />
          </Box>
        </Box>
      )}
    </>
  )
}

export default Formula
